'use client'
import { type FC, useCallback } from 'react'
import { Button, HStack, StackDivider, VStack } from '@chakra-ui/react'
import { BiReceipt } from 'react-icons/bi'
import { EmptyMessage, GrandHeading, GrandText } from 'ui'
import { formatAmount, getProductDiscountPrice } from 'shared-utils'
import { Currency, type CreateInvoiceOrderDTO, type OrderDTO, PaymentType } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useDispatch } from 'shared-redux'
import { cleanCart } from 'shared-redux/state'
import { useRouter } from 'next/navigation'
import { useGenericRequest } from 'api'
import { useBuyerShipping } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import { CheckoutSummaryItem } from '../../CheckoutSummaryItem'
import { VatNumberNotice } from '../../VatNumberNotice'

export interface InvoiceReviewPanelOverrides {
  invoiceReviewHeading?: string
  invoiceReviewBackToCart?: string
  invoiceReviewOrderPrefix?: string
  invoiceReviewOrderHelp?: string
  invoiceReviewOrderHelpProforma?: string
  invoiceReviewItemsHeading?: string
  invoiceReviewEmptyItems?: string
  invoiceReviewSubmitLabel?: string
  invoiceReviewLoadError?: string
  invoiceReviewNetTotal?: string
  invoiceReviewVat?: string
  invoiceReviewVatNoteReverseCharge?: string
  invoiceReviewVatNoteExport?: string
  invoiceReviewShippingExclVat?: string
  invoiceReviewShipping?: string
  invoiceReviewShippingInclVat?: string
  invoiceReviewShippingFree?: string
  invoiceReviewDiscount?: string
  invoiceReviewTotal?: string
  invoiceReviewTotalProforma?: string
  currency?: string
}

interface InvoiceReviewPanelProps {
  /** /api route mapped to PUT /storefront/invoice (flips DRAFT → CREATED). */
  submitPath: string
  /** Optional overrides to the persisted order before submit — used by carrier
   *  widgets to attach selected delivery option / shipping cost / address. */
  draftOverrides?: Partial<CreateInvoiceOrderDTO>
  /** Disables submit while a carrier widget is still collecting input. */
  isDisabled?: boolean
  /** Path factory for the post-submit redirect. */
  confirmationPathFactory?: (order: OrderDTO) => string
  /** Flushes any debounced draft sync the parent owns and awaits its in-flight
   *  update. Called before submit so the BE sees the latest FE state. */
  flushDraftSync?: () => Promise<void>
}

export const InvoiceReviewPanel: FC<InvoiceReviewPanelProps> = ({
  submitPath,
  draftOverrides,
  isDisabled,
  confirmationPathFactory,
  flushDraftSync
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const overrides = useOverridesContext<keyof InvoiceReviewPanelOverrides>()
  const { invoiceOrderResponse, orderLines, cartState, cartTotalDiscount, paymentProviderConfig } =
    useCheckoutContext()
  // `domesticRate` comes with it — the freight VAT split below has to use the
  // exact rate `submitInvoiceOrderHandler` uses, or the shopper approves one
  // VAT figure and the invoice is created with another.
  const { buyerVat, domesticRate } = useBuyerShipping(paymentProviderConfig)
  const { fetchNextRoute: submit, isLoading, error } = useGenericRequest<OrderDTO>()

  const renderCurrency = overrideText(Currency.SEK, overrides?.currency)

  // An invoice is an accounting document: every line shows the figure that will
  // actually be charged, to the cent, so the lines reconcile to the total. A
  // VAT split routinely lands on values like 78.6.
  //
  // Deliberately not `commaSeparator` + `priceMaximumFractionDigits`: that is a
  // storefront *display* preference (frequently 0) and it truncates rather than
  // rounds, so 78.6 renders as 78 — understating every line it touches.
  const money = useCallback(
    (n: number | undefined) => (n === undefined ? '-' : `${formatAmount(n)} ${renderCurrency}`),
    [renderCurrency]
  )

  const order = invoiceOrderResponse?.storefrontOrderResponse

  const displayShippingCost = draftOverrides?.shippingCost ?? order?.shippingCost ?? 0

  // Freight is quoted VAT-inclusive — by the carrier for Ingrid / Nshift /
  // PostNord, by `paymentShippingFactory` for the DEFAULT provider — and both
  // are already re-based onto this buyer before reaching here. Splitting the
  // VAT back out is therefore no new assumption: it is the same arithmetic the
  // submit handler applies when it persists `orderTaxAmount`.
  const shippingVatRate = buyerVat.resolveRate(domesticRate)
  const shippingVat = (displayShippingCost * shippingVatRate) / (100 + shippingVatRate)
  const shippingNet = displayShippingCost - shippingVat

  // Why this order carries no VAT. Reverse charge and export are 0 % for
  // different legal reasons and must be worded differently on the document.
  const vatNote = (() => {
    if (
      buyerVat.treatment === 'EU_REVERSE_CHARGE' ||
      buyerVat.treatment === 'DOMESTIC_REVERSE_CHARGE'
    ) {
      return overrideText(
        'VAT 0 % omvänd skattskyldighet (unionsintern försäljning)',
        overrides?.invoiceReviewVatNoteReverseCharge
      )
    }
    if (buyerVat.treatment === 'EXPORT') {
      return overrideText('VAT 0 % export utanför EU', overrides?.invoiceReviewVatNoteExport)
    }
    return null
  })()

  const handleSubmit = useCallback(async () => {
    if (!order?.orderNumber) return

    // Flush any pending debounced draft sync first so the BE sees the latest
    // FE state (e.g. shippingCost) before the submit handler does its GET.
    if (flushDraftSync) {
      try {
        await flushDraftSync()
      } catch {
        // Don't block submit on a sync failure — the BE still has the previous
        // draft state and the user can retry.
      }
    }

    // Submit handler is a server-side provisioning flow — it fetches the DRAFT,
    // pulls carrier state itself, and forces status=CREATED. The FE only needs
    // to identify which order to submit.
    const { unwrap } = submit(submitPath, {
      method: 'POST',
      body: JSON.stringify({ orderNumber: order.orderNumber })
    })

    unwrap()
      .then((submitted) => {
        dispatch(cleanCart())
        const path =
          confirmationPathFactory?.(submitted) ??
          `/confirmation?invoiceOrder=${submitted.orderNumber}`
        router.push(path)
      })
      .catch(() => {
        // Submission errors surface through the submit() hook's error state.
      })
  }, [order, submit, submitPath, dispatch, router, confirmationPathFactory, flushDraftSync])

  if (!order) {
    return (
      <EmptyMessage
        icon={<BiReceipt size={48} />}
        text={overrideText(
          'Beställning kunde inte laddas. Försök igen.',
          overrides?.invoiceReviewLoadError
        )}
      />
    )
  }

  const isProforma = order.paymentType === PaymentType.PROFORMA
  const orderGross = order.orderAmount ?? 0
  const orderTax = order.orderTaxAmount ?? 0
  // Items only. On a CREATED order `orderAmount` is the total with freight
  // inside it, but this is the DRAFT: `createInvoiceOrderHandler` computes its
  // totals before any carrier has quoted, and `submitInvoiceOrderHandler` is
  // what folds the freight cost and its VAT in. So the freight the shopper is
  // approving is still the local quote below, added on separately here.
  const itemsNet = orderGross - orderTax
  const orderNet = itemsNet + shippingNet
  const totalVat = orderTax + shippingVat
  const orderPrefix = overrideText('Order #', overrides?.invoiceReviewOrderPrefix)
  const orderHelp = isProforma
    ? overrideText(
        'En proformafaktura skickas till företaget. Beställningen levereras efter att betalningen har mottagits.',
        overrides?.invoiceReviewOrderHelpProforma
      )
    : overrideText(
        'Faktura skickas till företaget efter att beställningen är registrerad.',
        overrides?.invoiceReviewOrderHelp
      )
  const totalLabel = isProforma
    ? overrideText('Totalt att förskottsbetala', overrides?.invoiceReviewTotalProforma)
    : overrideText('Totalt att fakturera', overrides?.invoiceReviewTotal)

  return (
    <VStack align="stretch" spacing={6} w="full">
      <GrandHeading
        headingTag="h2"
        title={overrideText('Granska faktura', overrides?.invoiceReviewHeading)}
      />

      <GrandText color="text.mild">{`${orderPrefix}${order.orderNumber}. ${orderHelp}`}</GrandText>

      <VatNumberNotice paymentProviderConfig={paymentProviderConfig} />

      <VStack
        align="stretch"
        bg="background.surface"
        borderRadius="sm"
        divider={<StackDivider />}
        p={6}
        spacing={4}
      >
        <GrandHeading
          fontSize="md"
          headingTag="h3"
          title={overrideText('Artiklar', overrides?.invoiceReviewItemsHeading)}
        />

        {orderLines.length ? (
          // The BE strips items from POST/PUT responses on the invoice
          // endpoint family — we render straight from the local cart.
          orderLines.map((item) => {
            // Invoice review renders ex-VAT — match the BE flow (net item prices,
            // VAT shown separately) and the "Subtotal excl. VAT" line below.
            // `orderLines` carries the catalog `unitPrice`; the discount is
            // applied on the BE in `parseStoreToBackendOrderLines`. We need to
            // mirror it here so the per-line displayed total matches the BE-
            // computed Subtotal/VAT/Total.
            const discount = getProductDiscountPrice(item.product, cartState.cartDiscountCode)
            const netUnit =
              discount?.discountedPriceWithoutVat ??
              (item.vatRate ? item.unitPrice / (1 + item.vatRate / 100) : item.unitPrice)
            return (
              <HStack key={item.product.id} justify="space-between" w="full">
                <GrandText>{`${item.quantity} × ${item.product.name}`}</GrandText>
                <GrandText textAlign="right">{money(netUnit * item.quantity)}</GrandText>
              </HStack>
            )
          })
        ) : (
          <EmptyMessage text={overrideText('Inga artiklar', overrides?.invoiceReviewEmptyItems)} />
        )}

        {cartTotalDiscount ? (
          <CheckoutSummaryItem
            color="status.error"
            label={overrideText('Rabatt', overrides?.invoiceReviewDiscount)}
            value={money(-cartTotalDiscount)}
          />
        ) : null}
        {/* Net, so it sums with the item lines into the ex-VAT subtotal below
            rather than double-counting its own VAT against it. */}
        <CheckoutSummaryItem
          label={`${overrideText('Frakt', overrides?.invoiceReviewShipping)} (${overrideText(
            'exkl. moms',
            overrides?.invoiceReviewShippingExclVat
          )})`}
          value={
            displayShippingCost
              ? money(shippingNet)
              : overrideText('Fri frakt', overrides?.invoiceReviewShippingFree)
          }
        />
        <CheckoutSummaryItem
          label={overrideText('Summa exkl. moms', overrides?.invoiceReviewNetTotal)}
          value={money(orderNet)}
        />
        <CheckoutSummaryItem
          label={overrideText('Moms', overrides?.invoiceReviewVat)}
          value={money(totalVat)}
        />
        {/* The legal basis for a 0 % line. The merchant's own invoices carry
            this text, and an intra-community supply is only defensible to the
            tax authority when the document states it. */}
        {vatNote ? (
          <GrandText color="text.mild" fontSize="sm">
            {vatNote}
          </GrandText>
        ) : null}
        <CheckoutSummaryItem
          fontSize="lg"
          fontWeight="bold"
          label={totalLabel}
          value={money(orderGross + displayShippingCost)}
        />
      </VStack>

      {error ? (
        <GrandText color="status.error" fontSize="sm">
          {error.message}
        </GrandText>
      ) : null}

      <Button
        aria-label="submit-invoice-order"
        isDisabled={!!isDisabled}
        isLoading={isLoading}
        onClick={() => void handleSubmit()}
        alignSelf="flex-end"
      >
        {overrideText('Skicka beställning', overrides?.invoiceReviewSubmitLabel)}
      </Button>
    </VStack>
  )
}

export default InvoiceReviewPanel
